/**
 * 詳細レポートの検出要素の一覧 (docs/27 §9)。
 *
 * 2026-09-16 (オーナー指示): マスタから解決した値はすべて色付きチップで示す (以前は調査項目が
 * グレーの小さな文字で埋もれていた)。カード表示と表表示を切り替えられる (表は一覧性を優先し、
 * 順位・各調査項目・方角・距離・根拠を横に並べる)。チップの色はマスタの `colorAxis` に従う
 * (地図のピン・ドーナツと同じ `resolveChipColor`)。
 */
import { useState } from 'react';
import type { EvaluationAxisDef } from '@citizen-walk/shared-types';
import type { PrimaryAxisResolution, StreetSurveyClassification } from './streetSurveyAxisTypes';
import type { RoleTypeAxisResolution } from './streetSurveyAxisTypes';
import type { ResolvedSurveyAxis } from './useStreetSurveyConfig';
import type { StreetSurveyPost } from './useStreetSurveyPosts';
import { resolveChipColor } from '../posts-moderation';
import {
  resolveClassificationTitle,
  resolveMasterRow,
  resolvePinLocation,
  resolvePinRoleColor,
  resolvePinRoleEmoji,
  resolveReasonText,
} from './streetSurveyPinDisplay';

export interface StreetSurveyRankedListProps {
  post: StreetSurveyPost;
  classifications: StreetSurveyClassification[];
  primaryAxis: PrimaryAxisResolution;
  roleTypeAxis?: RoleTypeAxisResolution;
  /**
   * 表示する調査項目 (実効の出力フォーマット由来)。渡すと各検出要素の全項目をチップで出す。
   * 未指定なら従来どおり主軸 (primaryAxis) と評価だけを出す。
   */
  axes?: ResolvedSurveyAxis[];
  highlightedRank?: number | null;
  /** 選択中の項目 (`rank === highlightedRank`) を再クリックすると null を渡してトグル解除する
   *  (2026-07-21、ユーザー要望)。 */
  onSelectRank?: (rank: number | null) => void;
  /** 実効の出力フォーマットの reasoningField (新方式の地点は解析時に記録した根拠の列を優先、`resolveReasonText`)。
   * モードごとに根拠コメントのキー名が異なるため、決め打ちにせず呼出側から渡す (2026-07-14)。 */
  reasoningField?: string;
  /** docs/27 §9: 評価軸 (ポジ / ネガ / 中立)。あれば各検出要素に評価バッジを出す。 */
  evaluation?: EvaluationAxisDef | null;
  /** 各検出要素に追加で表示する列 (机上調査の設定 `listFieldKeys`)。 */
  listFieldKeys?: readonly string[];
}

const FALLBACK_COLOR = '#94A3B8';

interface ChipValue {
  key: string;
  axisLabel: string;
  label: string;
  color: string;
  /** 評価の項目か (表の並び順・見出しで区別する)。 */
  isEvaluation: boolean;
}

/** 1 件の検出要素について、各調査項目の値をチップの形に解決する (マスタに無い値はそのまま出す)。 */
function resolveChips(
  c: StreetSurveyClassification,
  axes: readonly ResolvedSurveyAxis[],
  evaluation: EvaluationAxisDef | null,
): ChipValue[] {
  const chips: ChipValue[] = [];
  for (const axis of axes) {
    const raw = c[axis.codeField];
    const value = raw === undefined || raw === null ? '' : String(raw);
    const isEvaluation = !!evaluation && axis.codeField === evaluation.field;
    if (!value) continue;
    if (isEvaluation) {
      chips.push({
        key: axis.key,
        axisLabel: axis.label,
        label: evaluation!.labelOf(raw) || value,
        color: evaluation!.colorOf(raw),
        isEvaluation: true,
      });
      continue;
    }
    const row = resolveMasterRow(value, axis.byCategoryCd);
    const nameFromField = axis.nameField ? String(c[axis.nameField] ?? '') : '';
    chips.push({
      key: axis.key,
      axisLabel: axis.label,
      label: row?.category || nameFromField || value,
      color: row ? resolveChipColor(row, axis.master ?? undefined) : FALLBACK_COLOR,
      isEvaluation: false,
    });
  }
  return chips;
}

function Chip({ chip, withAxisLabel }: { chip: ChipValue; withAxisLabel?: boolean }) {
  return (
    <span
      className="inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold text-white"
      style={{ backgroundColor: chip.color }}
      title={`${chip.axisLabel}: ${chip.label}`}
    >
      {withAxisLabel && <span className="font-normal opacity-80">{chip.axisLabel}</span>}
      <span className="truncate">{chip.label}</span>
    </span>
  );
}

export function StreetSurveyRankedList({
  post,
  classifications,
  primaryAxis,
  roleTypeAxis,
  axes,
  highlightedRank,
  onSelectRank,
  reasoningField,
  evaluation = null,
  listFieldKeys = [],
}: StreetSurveyRankedListProps) {
  const [view, setView] = useState<'card' | 'table'>('card');
  const sorted = [...classifications].sort((a, b) => a.rank - b.rank);

  if (sorted.length === 0) {
    return <p className="text-sm text-slate-400 mt-4">検出要素はありません。</p>;
  }

  // 表の列は「値を持つ項目」に絞る (この地点で 1 件も値が無い項目は列を作らない)。
  const shownAxes = (axes ?? []).filter((axis) =>
    sorted.some((c) => {
      const raw = c[axis.codeField];
      return raw !== undefined && raw !== null && String(raw) !== '';
    }),
  );

  // 調査項目が渡されていないとき (旧呼出) は主軸だけをチップにする。
  const fallbackAxes: ResolvedSurveyAxis[] = primaryAxis.primaryAxis
    ? [
        {
          key: primaryAxis.primaryAxis.key ?? primaryAxis.primaryAxis.codeField,
          label: primaryAxis.primaryAxis.label,
          codeField: primaryAxis.primaryAxis.codeField,
          nameField: primaryAxis.primaryAxis.nameField,
          masterId: primaryAxis.primaryAxis.masterId ?? null,
          role: primaryAxis.primaryAxis.role,
          explicit: true,
          axis: primaryAxis.primaryAxis,
          master: primaryAxis.master,
          byCategoryCd: primaryAxis.byCategoryCd,
        },
      ]
    : [];

  const rows = sorted.map((c) => {
    const primaryCode = String(c[primaryAxis.primaryAxis?.codeField ?? ''] ?? '');
    const primaryRow = resolveMasterRow(primaryCode, primaryAxis.byCategoryCd);
    return {
      c,
      chips: resolveChips(c, shownAxes.length > 0 ? shownAxes : fallbackAxes, evaluation),
      color: primaryRow
        ? resolveChipColor(primaryRow, primaryAxis.master ?? undefined)
        : FALLBACK_COLOR,
      title: resolveClassificationTitle(c as unknown as Record<string, unknown>, primaryAxis),
      location: resolvePinLocation(post, c.rank),
      roleColor: roleTypeAxis ? resolvePinRoleColor(post, roleTypeAxis, c.rank) : undefined,
      roleEmoji: roleTypeAxis ? resolvePinRoleEmoji(post, roleTypeAxis, c.rank) : undefined,
      roleName: String(c[roleTypeAxis?.axis?.codeField ?? ''] ?? '') || undefined,
      // 追加で表示する列は設定 (`listFieldKeys`) で選ぶ (assessment_level / score などの列名を決め打ちしない)。
      levelOrScore:
        listFieldKeys
          .map((k) => c[k])
          .filter((v) => v !== undefined && v !== null && String(v).trim() !== '')
          .map((v) => String(v))
          .join('・') || null,
      reasoning: resolveReasonText(post, c as unknown as Record<string, unknown>, reasoningField),
    };
  });

  const viewToggle = (
    <div className="mt-4 flex items-center justify-end gap-1 text-xs">
      <span className="mr-1 text-slate-500">検出要素の表示</span>
      {(
        [
          ['card', 'カード'],
          ['table', '表'],
        ] as const
      ).map(([id, label]) => (
        <button
          key={id}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setView(id);
          }}
          className={`rounded border px-2 py-0.5 ${
            view === id
              ? 'border-primary-400 bg-primary-50 text-primary-700'
              : 'border-slate-300 bg-white text-slate-600'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  if (view === 'table') {
    return (
      <>
        {viewToggle}
        <div className="mt-2 overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full min-w-[40rem] border-collapse text-left text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-2 py-1.5 font-semibold">順位</th>
                {shownAxes.length > 0 ? (
                  shownAxes.map((a) => (
                    <th key={a.key} className="px-2 py-1.5 font-semibold">
                      {a.label}
                    </th>
                  ))
                ) : (
                  <th className="px-2 py-1.5 font-semibold">
                    {primaryAxis.primaryAxis?.label ?? '調査項目'}
                  </th>
                )}
                <th className="px-2 py-1.5 font-semibold">方角・距離</th>
                <th className="px-2 py-1.5 font-semibold">根拠</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.c.rank}
                  onClick={
                    onSelectRank
                      ? (e) => {
                          e.stopPropagation();
                          onSelectRank(r.c.rank === highlightedRank ? null : r.c.rank);
                        }
                      : undefined
                  }
                  className={`border-t border-slate-100 align-top ${
                    onSelectRank ? 'cursor-pointer hover:bg-slate-50' : ''
                  } ${r.c.rank === highlightedRank ? 'bg-primary-50' : ''}`}
                >
                  <td className="px-2 py-1.5">
                    <span
                      className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold text-white"
                      style={{ backgroundColor: r.color }}
                    >
                      {r.c.rank}
                    </span>
                  </td>
                  {(shownAxes.length > 0
                    ? shownAxes.map((a) => r.chips.find((chip) => chip.key === a.key) ?? null)
                    : [r.chips[0] ?? null]
                  ).map((chip, i) => (
                    <td key={i} className="min-w-0 px-2 py-1.5">
                      {chip ? <Chip chip={chip} /> : <span className="text-slate-400">—</span>}
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-slate-600">
                    {r.location
                      ? `${r.location.directionLabel} (${Math.round(r.location.heading)}°) / ${r.location.distanceMeters}m`
                      : '—'}
                  </td>
                  <td className="min-w-0 px-2 py-1.5 text-slate-600">{r.reasoning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  return (
    <>
      {viewToggle}
      <ul className="mt-2 grid grid-cols-1 gap-2">
        {rows.map((r) => {
          const isHighlighted = r.c.rank === highlightedRank;
          return (
            <li
              key={r.c.rank}
              onClick={
                onSelectRank
                  ? (e) => {
                      // ページ側の「他の場所をクリックしたら選択解除」ハンドラへの伝播を止める
                      // (2026-07-21、StreetSurveyDetailPage.tsx の外側クリック解除機能追加時に対応)。
                      e.stopPropagation();
                      // 選択中の項目を再クリックしたらトグル解除する (2026-07-21、ユーザー要望)。
                      onSelectRank(isHighlighted ? null : r.c.rank);
                    }
                  : undefined
              }
              className={`rounded-lg border p-3 ${onSelectRank ? 'cursor-pointer hover:border-primary-300' : ''} ${
                isHighlighted ? 'border-primary-400 bg-primary-50' : 'border-slate-200 bg-white'
              }`}
            >
              <div className="flex items-start gap-3">
                <span
                  className="shrink-0 mt-0.5 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
                  style={{ backgroundColor: r.color }}
                >
                  {r.c.rank}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-800">{r.title}</p>
                    {r.roleColor && (
                      <span
                        className="shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold text-white"
                        style={{ backgroundColor: r.roleColor }}
                      >
                        {r.roleEmoji && <span aria-hidden="true">{r.roleEmoji}</span>}
                        {r.roleName}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    {r.chips.map((chip) => (
                      <Chip key={chip.key} chip={chip} withAxisLabel />
                    ))}
                    {r.levelOrScore && (
                      <span className="rounded-full border border-slate-300 px-2 py-0.5 text-[11px] text-slate-600">
                        {r.levelOrScore}
                      </span>
                    )}
                    {r.location && (
                      <span className="rounded-full border border-slate-300 px-2 py-0.5 text-[11px] text-slate-600">
                        {r.location.directionLabel} ({Math.round(r.location.heading)}°) ／{' '}
                        {r.location.distanceMeters}m
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-slate-600">{r.reasoning}</p>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {evaluation && (
        <p className="mt-1 text-[11px] text-slate-500">
          評価の色: {evaluation.polarityLabels.positive} / {evaluation.polarityLabels.negative} /{' '}
          {evaluation.polarityLabels.neutral} は{evaluation.label}の値に対応します。
        </p>
      )}
    </>
  );
}
